import { Tooltip } from "antd"
import React from "react"

import type { AnalyticsTopSalespersonRow } from "../redux/global-map.types"
import { ANALYTICS_ENTITY_COLOR } from "./analytics-constants"

interface TopSalespeopleCardProps {
  rows: AnalyticsTopSalespersonRow[]
  onSelectSalesperson: (userId: string) => void
}

const SEGMENT_ORDER = ["project", "lead", "subcontractor", "appointment"]

function initials(name: string | null): string {
  if (!name) return "?"
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?"
}

/**
 * Spec §8: top 5 by record count (already capped server-side), the
 * explicit `sales_manager` relationship (not record creator). "Unassigned"
 * is server-provided and not clickable — no single salesperson id to apply.
 */
export function TopSalespeopleCard({
  rows,
  onSelectSalesperson
}: TopSalespeopleCardProps) {
  const maxTotal = Math.max(...rows.map((row) => row.total), 1)

  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {rows.map((row) => {
        const isClickable = row.userId !== null
        const rowContent = (
          <>
            <span
              aria-hidden
              className="flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600"
            >
              {initials(row.name)}
            </span>
            {/* A long name truncates inside its own fixed column and keeps
                its full value on hover — it can never push the bar or the
                total out of the card. */}
            <Tooltip title={row.name}>
              <span className="w-24 shrink-0 truncate text-[13px] text-slate-700">
                {row.name}
              </span>
            </Tooltip>
            <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div
                className="flex h-full overflow-hidden rounded-full"
                style={{ width: `${(row.total / maxTotal) * 100}%` }}
              >
                {SEGMENT_ORDER.filter((key) => row.byEntityType[key]).map(
                  (key) => (
                    <div
                      key={key}
                      style={{
                        width: `${(row.byEntityType[key] / row.total) * 100}%`,
                        backgroundColor: ANALYTICS_ENTITY_COLOR[key]
                      }}
                    />
                  )
                )}
              </div>
            </div>
            <span className="w-7 shrink-0 text-right text-[13px] font-semibold text-slate-900">
              {row.total}
            </span>
          </>
        )

        return (
          <li key={row.userId ?? "unassigned"}>
            {isClickable ? (
              <button
                type="button"
                onClick={() => row.userId && onSelectSalesperson(row.userId)}
                className="flex w-full items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-slate-50"
              >
                {rowContent}
              </button>
            ) : (
              <div className="flex w-full items-center gap-2.5 px-1.5 py-1.5">
                {rowContent}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
